/**
 * 固件升级总流程编排，与旧项目 upgrading.html 对齐。
 */

import type { FirmwareTab, UpgradeCallbacks } from './firmware-types'
import { buildFirmwareUrl } from './firmware-mock-data'
import {
  getEnterUpgradePacketForTab,
  findDeviceForConnectionType,
  getConnectionTypeByProductId,
  getRequiredConnectionTypeByTab,
  isNormalModeDevice,
  isUpgradeModeDevice,
  needsDeviceReconnect,
  openDeviceIfNeeded,
  resolveConnectionTypeFromDevices,
  shouldUseSbtOta,
  sleep,
  waitForUpgradeDevice,
} from './firmware-device'
import { startSimpleUpgrade, cancelSimpleUpgrade, loadFirmwareFile } from './simple-firmware-upgrade'
import { startSbtOtaUpgrade, cancelSbtOtaUpgrade } from './sbt-ota-upgrade'

export type { FirmwareTab, UpgradeCallbacks }

type ConnectionType = 'USB' | '2.4G' | ''

interface ConnectionValidation {
  ok: boolean
  actualType: ConnectionType
  requiredType: 'USB' | '2.4G'
  sourceDevice: HIDDevice | null
  message?: string
}

interface UpgradeOptions {
  firmwareFile?: File
  firmwareUrl?: string
  hidDevice?: HIDDevice | null
}

let hasTriggeredEnterUpgrade = false

export async function fetchFirmwareFromUrl(url: string): Promise<Uint8Array> {
  const ts = Date.now()
  const sep = url.includes('?') ? '&' : '?'
  const res = await fetch(`${url}${sep}_t=${ts}`, { cache: 'no-store' })
  if (!res.ok) {
    throw new Error(`Firmware download failed: ${res.status} ${res.statusText}`)
  }

  const firmware = new Uint8Array(await res.arrayBuffer())
  if (!firmware.length) throw new Error('Firmware file is empty')
  return firmware
}

export function validateConnectionForTab(
  tab: FirmwareTab,
  devices: HIDDevice[],
  preferredDevice?: HIDDevice | null
): ConnectionValidation {
  // 耳机、蓝牙、降噪要求 USB；Dongle 要求 2.4G。先按目标连接类型筛选，
  // 再返回实际连接类型，供页面显示明确的条件不满足提示。
  const requiredType = getRequiredConnectionTypeByTab(tab)
  const matchingDevice = findDeviceForConnectionType(devices, requiredType, preferredDevice)
  if (matchingDevice) {
    return {
      ok: true,
      actualType: requiredType,
      requiredType,
      sourceDevice: matchingDevice,
    }
  }

  const { actualType, sourceDevice } = resolveConnectionTypeFromDevices(devices, preferredDevice)

  if (!actualType) {
    return {
      ok: false,
      actualType,
      requiredType,
      sourceDevice,
      message: 'Unable to identify connection type. Please confirm the device is connected correctly.',
    }
  }

  return {
    ok: actualType === requiredType,
    actualType,
    requiredType,
    sourceDevice,
    message:
      actualType === requiredType
        ? undefined
        : `${tab} upgrade requires ${requiredType}, current connection is ${actualType}.`,
  }
}

function getUpgradeTargetForTab(_tab: FirmwareTab): number {
  return 0
}

async function enterUpgradeModeIfPossible(
  normalDevice: HIDDevice,
  tab: FirmwareTab,
  onStatus?: (msg: string) => void
): Promise<boolean> {
  if (!normalDevice || hasTriggeredEnterUpgrade) return false

  const packet = getEnterUpgradePacketForTab(tab)
  if (!packet) return false

  hasTriggeredEnterUpgrade = true

  try {
    await openDeviceIfNeeded(normalDevice)
    onStatus?.('Sending enter-upgrade command...')
    const fullPacket = new Uint8Array(63)
    fullPacket.set(packet)
    await normalDevice.sendReport(49, fullPacket)
    onStatus?.('Command sent. Waiting for upgrade-mode device...')
    return true
  } catch {
    hasTriggeredEnterUpgrade = false
    return false
  }
}

async function ensureUpgradeDevice(
  tab: FirmwareTab,
  onStatus?: (msg: string) => void,
  preferredDevice?: HIDDevice | null
): Promise<HIDDevice | null> {
  // 不重启的蓝牙/降噪直接复用正常模式设备；耳机/Dongle 则先下发进入升级命令，
  // 等待设备重启并重新暴露 reportId=0 的升级模式接口。
  if (!navigator.hid) return null

  const requiredType = getRequiredConnectionTypeByTab(tab)
  const devices = await navigator.hid.getDevices()
  let upgradeDevice: HIDDevice | undefined =
    preferredDevice && isUpgradeModeDevice(preferredDevice) && getConnectionTypeByProductId(preferredDevice.productId) === requiredType
      ? preferredDevice
      : undefined
  let normalDevice: HIDDevice | undefined =
    preferredDevice && isNormalModeDevice(preferredDevice) && getConnectionTypeByProductId(preferredDevice.productId) === requiredType
      ? preferredDevice
      : undefined

  for (const dev of devices) {
    if (getConnectionTypeByProductId(dev.productId) !== requiredType) continue
    if (!upgradeDevice && isUpgradeModeDevice(dev)) upgradeDevice = dev
    if (!normalDevice && isNormalModeDevice(dev)) normalDevice = dev
  }

  if (!needsDeviceReconnect(tab)) {
    if (!normalDevice) return null
    await openDeviceIfNeeded(normalDevice)
    return normalDevice
  }

  if (!upgradeDevice && normalDevice) {
    hasTriggeredEnterUpgrade = false
    const didSend = await enterUpgradeModeIfPossible(normalDevice, tab, onStatus)
    if (!didSend) return null
    await sleep(2000)
    upgradeDevice = (await waitForUpgradeDevice(15000, requiredType)) ?? undefined
  }

  if (!upgradeDevice) return null
  await openDeviceIfNeeded(upgradeDevice)
  return upgradeDevice
}

/**
 * 执行一次完整的固件升级。
 *
 * 设备规则与旧项目 upgrading.html 保持一致：
 * - 耳机和 Dongle 重启进入升级模式后，使用 reportId 0 的简化 OTA。
 * - 降噪和蓝牙不重启，保持使用 reportId 49，并在注册监听器后由 SBT OTA 发送 0x85/0x86。
 */
export async function runFirmwareUpgrade(
  tab: FirmwareTab,
  version: string,
  callbacks: UpgradeCallbacks = {},
  options: UpgradeOptions = {}
): Promise<void> {
  hasTriggeredEnterUpgrade = false

  const onStatus = (msg: string) => callbacks.onLog?.(msg, 'info')

  if (!navigator.hid) {
    throw new Error('Current browser does not support WebHID')
  }

  const devices = await navigator.hid.getDevices()
  const connCheck = validateConnectionForTab(tab, devices, options.hidDevice)
  if (!connCheck.ok) {
    throw new Error(connCheck.message || 'Connection type mismatch')
  }

  onStatus('Detecting device...')
  const hidDevice = await ensureUpgradeDevice(tab, onStatus, connCheck.sourceDevice || options.hidDevice)
  if (!hidDevice) {
    throw new Error('No available upgrade device found. Please reconnect the device and try again.')
  }

  onStatus('Downloading firmware...')
  let firmware: Uint8Array
  if (options.firmwareFile) {
    firmware = await loadFirmwareFile(options.firmwareFile)
  } else {
    const url = options.firmwareUrl || buildFirmwareUrl(tab, version)
    if (!url) throw new Error('Invalid firmware URL')
    firmware = await fetchFirmwareFromUrl(url)
  }

  onStatus('Upgrading. Keep the device connected...')

  const upgradeCallbacks: UpgradeCallbacks = {
    onProgress: callbacks.onProgress,
    onComplete: callbacks.onComplete,
    onError: callbacks.onError,
    onLog: callbacks.onLog,
  }

  if (shouldUseSbtOta(tab)) {
    const enterCmd = getEnterUpgradePacketForTab(tab)
    await startSbtOtaUpgrade(
      hidDevice,
      firmware,
      upgradeCallbacks,
      getUpgradeTargetForTab(tab),
      {},
      enterCmd
    )
  } else {
    await startSimpleUpgrade(hidDevice, firmware, upgradeCallbacks)
  }
}

export function cancelFirmwareUpgrade(): void {
  cancelSimpleUpgrade()
  cancelSbtOtaUpgrade()
  hasTriggeredEnterUpgrade = false
}

export async function pickHidDeviceManually(vendorId?: number): Promise<HIDDevice | null> {
  if (!navigator.hid) return null
  const filters = vendorId != null ? [{ vendorId }] : []
  const picked = await navigator.hid.requestDevice({ filters })
  return picked.find(dev => isUpgradeModeDevice(dev)) || picked[0] || null
}
